import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  CallEnd as EndIcon,
  Mic as MicIcon,
  Videocam as CamIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';

/**
 * Live Join Video Call room — embeds Jitsi Meet (video + audio).
 * Instructors can end the session; learners join the same room URL.
 */
export default function TrainingLiveRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [ending, setEnding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/training/live-sessions/${sessionId}`);
      setSession(r.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Cannot open live session');
      navigate('/training/my-training');
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const jitsiSrc = useMemo(() => {
    if (!session?.jitsi_room && !session?.jitsi_url) return null;
    const display = encodeURIComponent(session.display_name || 'Participant');
    const subject = encodeURIComponent(
      session.meeting_subject ||
        [session.title?.course_code, session.title?.edition_number]
          .filter(Boolean)
          .join(' - ') ||
        session.title?.name ||
        'ERP4C Training'
    );
    // hash config: start with AV unmuted request; users still confirm browser permissions
    const recordingMode = session.recording_mode || 'cloud';
    const allowLocal = recordingMode === 'local' || recordingMode === 'both';
    const allowCloud = recordingMode === 'cloud' || recordingMode === 'both';
    const config = [
      `userInfo.displayName="${display}"`,
      `config.subject="${subject}"`,
      'config.startWithAudioMuted=false',
      'config.startWithVideoMuted=false',
      'config.prejoinConfig.enabled=true',
      'config.disableDeepLinking=true',
      allowLocal
        ? 'config.localRecording.disable=false'
        : 'config.localRecording.disable=true',
      allowCloud
        ? 'config.fileRecordingsEnabled=true'
        : 'config.fileRecordingsEnabled=false',
      'config.fileRecordingsServiceEnabled=false',
    ].join('&');
    // Prefer backend jitsi_url (correct scheme for local http://127.0.0.1 vs Azure https://…)
    if (session.jitsi_url) {
      const base = String(session.jitsi_url).split('#')[0].replace(/\/$/, '');
      return `${base}#${config}`;
    }
    const domain = session.jitsi_domain || 'meet.jit.si';
    const local = /^(127\.0\.0\.1|localhost)(:|$)/i.test(domain);
    const scheme = local ? 'http' : 'https';
    return `${scheme}://${domain}/${encodeURIComponent(session.jitsi_room)}#${config}`;
  }, [session]);

  const endSession = async () => {
    if (!window.confirm('End this live session for everyone?')) return;
    setEnding(true);
    try {
      await api.post(`/training/live-sessions/${sessionId}/end`);
      toast.success('Session ended');
      navigate('/training/instructor');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to end session');
    } finally {
      setEnding(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) return null;

  const isInstructor = session.role === 'instructor';
  const ended = session.status === 'ended';

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', minHeight: 480 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            {session.meeting_subject || session.contentItem?.label || 'Join Video Call'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {session.title?.name || 'Course'}
            {session.status === 'live' ? ' · Live' : ` · ${session.status}`}
            {isInstructor ? ' · You are the instructor' : ' · Learner'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(isInstructor ? '/training/instructor' : '/training/my-training')}
          >
            Back
          </Button>
          {isInstructor && !ended && (
            <Button
              size="small"
              color="error"
              variant="contained"
              startIcon={<EndIcon />}
              disabled={ending}
              onClick={endSession}
            >
              End session
            </Button>
          )}
        </Stack>
      </Stack>

      <Alert severity="info" icon={<CamIcon />} sx={{ mb: 1 }}>
        Allow camera and microphone when prompted. Use the toolbar for mute and screen share.
        {isInstructor
          ? session.recording_mode === 'local'
            ? ' Recording mode: Allow to Record Locally (browser).'
            : session.recording_mode === 'both'
              ? ' Recording mode: Both — local browser and/or ERP4C Cloud/Server (Jibri).'
              : ' Recording mode: Allow to Record on the ERP4C Cloud/Server (Jibri → uploads PVC).'
          : ' Recording is available to the instructor (moderator) only.'}
        <MicIcon fontSize="inherit" sx={{ verticalAlign: 'middle', mx: 0.5 }} />
      </Alert>

      {ended ? (
        <Alert severity="warning">This session has ended.</Alert>
      ) : (
        <Box
          sx={{
            flex: 1,
            borderRadius: 2,
            overflow: 'hidden',
            border: 1,
            borderColor: 'divider',
            bgcolor: '#111',
            minHeight: 420,
          }}
        >
          {jitsiSrc ? (
            <Box
              component="iframe"
              ref={iframeRef}
              title="Training live video call"
              src={jitsiSrc}
              allow="camera; microphone; display-capture; autoplay; clipboard-write"
              sx={{ width: '100%', height: '100%', border: 0, minHeight: 420 }}
            />
          ) : (
            <Typography color="common.white" sx={{ p: 3 }}>
              Room not ready.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
